import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGameMusic } from "../api/api_hooks";

const VOLUME_KEY = "game-music-volume";
const MUTED_KEY = "game-music-muted";
const EMPTY_TRACKS = [];

function readStoredVolume() {
  const storedValue = window.localStorage.getItem(VOLUME_KEY);
  if (storedValue === null) return 0.65;
  const value = Number(storedValue);
  return Number.isFinite(value) && value >= 0 && value <= 1 ? value : 0.65;
}

function readStoredMuted() {
  return window.localStorage.getItem(MUTED_KEY) === "true";
}

export function weightedShuffle(tracks, random = Math.random) {
  const remaining = [...tracks];
  const shuffled = [];

  while (remaining.length) {
    const weights = remaining.map((track) => Math.max(Number(track.match_score) || 0, 1));
    const totalWeight = weights.reduce((total, weight) => total + weight, 0);
    let selection = random() * totalWeight;
    let selectedIndex = weights.length - 1;

    for (let index = 0; index < weights.length; index += 1) {
      selection -= weights[index];
      if (selection < 0) {
        selectedIndex = index;
        break;
      }
    }

    shuffled.push(remaining.splice(selectedIndex, 1)[0]);
  }

  return shuffled;
}

export function filterTracks(tracks, query) {
  const normalizedQuery = String(query || "").trim().toLocaleLowerCase();
  if (!normalizedQuery) return [];

  return tracks.filter((track) => {
    const titleMatches = String(track.title || "")
      .toLocaleLowerCase()
      .includes(normalizedQuery);
    const artistMatches = String(track.artist || "")
      .toLocaleLowerCase()
      .includes(normalizedQuery);
    const tagMatches = (track.tags || []).some((tag) =>
      String(tag).toLocaleLowerCase().includes(normalizedQuery)
    );
    return titleMatches || artistMatches || tagMatches;
  });
}

export function formatTrackLabel(track) {
  const title = String(track?.title || "").trim();
  const artist = String(track?.artist || "").trim();
  return artist ? `${title} — ${artist}` : title;
}

export default function MusicPlayer({ gameId, onSearchFocusChange, onRemoteStateChange }) {
  const { data } = useGameMusic(gameId);
  const tracks = data || EMPTY_TRACKS;
  const audioRef = useRef(null);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [volume, setVolume] = useState(readStoredVolume);
  const [muted, setMuted] = useState(readStoredMuted);
  const [isHovered, setIsHovered] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const volumeRef = useRef(volume);
  const mutedRef = useRef(muted);

  const currentTrack = playlist[currentIndex] || null;
  const isExpanded = isHovered || hasFocus;
  const searchResults = useMemo(
    () => filterTracks(tracks, searchQuery),
    [searchQuery, tracks]
  );

  useEffect(() => {
    setPlaylist(weightedShuffle(tracks));
    setCurrentIndex(0);
    setIsPlaying(false);
    setAutoplayBlocked(false);
  }, [tracks]);

  const loadAndPlay = useCallback((track) => {
    const audio = audioRef.current;
    if (!audio || !track) return;

    audio.dataset.trackId = String(track.id);
    audio.pause();
    audio.src = track.audio_file;
    audio.volume = volumeRef.current;
    audio.muted = mutedRef.current;
    audio.loop = true;
    audio.load();

    const playResult = audio.play();
    if (playResult?.then) {
      playResult
        .then(() => {
          setIsPlaying(true);
          setAutoplayBlocked(false);
        })
        .catch(() => {
          setIsPlaying(false);
          setAutoplayBlocked(true);
        });
    }
  }, []);

  useEffect(() => {
    if (currentTrack && audioRef.current?.dataset.trackId !== String(currentTrack.id)) {
      loadAndPlay(currentTrack);
    }
  }, [currentTrack, loadAndPlay]);

  useEffect(() => {
    const audio = audioRef.current;
    volumeRef.current = volume;
    if (audio) audio.volume = volume;
    window.localStorage.setItem(VOLUME_KEY, String(volume));
  }, [volume]);

  useEffect(() => {
    const audio = audioRef.current;
    mutedRef.current = muted;
    if (audio) audio.muted = muted;
    window.localStorage.setItem(MUTED_KEY, String(muted));
  }, [muted]);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      if (!audio) return;
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    };
  }, []);

  useEffect(() => {
    return () => onSearchFocusChange?.(false);
  }, [onSearchFocusChange]);

  const selectTrack = useCallback(
    (index) => {
      if (!playlist.length) return;
      const normalizedIndex = (index + playlist.length) % playlist.length;
      setCurrentIndex(normalizedIndex);
      loadAndPlay(playlist[normalizedIndex]);
    },
    [loadAndPlay, playlist]
  );

  const togglePlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      audio.play()
        .then(() => {
          setIsPlaying(true);
          setAutoplayBlocked(false);
        })
        .catch(() => setAutoplayBlocked(true));
    } else {
      audio.pause();
      setIsPlaying(false);
    }
  }, []);

  const selectSearchResult = (track) => {
    const index = playlist.findIndex((item) => item.id === track.id);
    if (index < 0) return;
    selectTrack(index);
    setSearchQuery("");
  };

  useEffect(() => {
    onRemoteStateChange?.(
      {
        currentTrack: formatTrackLabel(currentTrack),
        currentTrackId: currentTrack?.id ? String(currentTrack.id) : "",
        isPlaying,
        autoplayBlocked,
        muted,
        volume,
        tracks: playlist.map((track) => ({ value: String(track.id), label: formatTrackLabel(track) })),
      },
      {
        previous: () => selectTrack(currentIndex - 1),
        next: () => selectTrack(currentIndex + 1),
        play: () => {
          if (audioRef.current?.paused) togglePlayback();
        },
        pause: () => {
          if (audioRef.current && !audioRef.current.paused) togglePlayback();
        },
        togglePlayback,
        setMuted,
        setVolume,
        selectTrackId: (trackId) => {
          const index = playlist.findIndex((track) => String(track.id) === String(trackId));
          if (index >= 0) selectTrack(index);
        },
      }
    );
  }, [autoplayBlocked, currentIndex, currentTrack, isPlaying, muted, onRemoteStateChange, playlist, selectTrack, togglePlayback, volume]);

  return (
    <>
      <audio
        ref={audioRef}
        loop
        preload="auto"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      {currentTrack && (
        <section
          className={`pointer-events-auto absolute bottom-4 left-4 z-40 overflow-hidden border border-white/15 bg-slate-950/90 text-left text-white shadow-2xl backdrop-blur transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-400 ${
            isExpanded
              ? "max-h-[360px] w-[min(92vw,460px)] rounded-2xl p-3"
              : "h-12 max-h-12 w-12 cursor-pointer rounded-full p-0"
          }`}
          aria-label="Game music player"
          tabIndex={0}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onFocusCapture={() => setHasFocus(true)}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setHasFocus(false);
          }}
        >
          {!isExpanded ? (
            <div className="flex h-full w-full items-center justify-center" aria-hidden="true">
              <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current" focusable="false">
                <path d="M9 18V5l11-2v13.5a3.5 3.5 0 1 1-2-3.16V7.06L11 8.33V18.5A3.5 3.5 0 1 1 9 18Z" />
              </svg>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => selectTrack(currentIndex - 1)} aria-label="Previous track" className="rounded-lg px-2 py-1 hover:bg-white/10">
                  &#9198;
                </button>
                <button type="button" onClick={togglePlayback} aria-label={isPlaying ? "Pause music" : "Play music"} className="rounded-full bg-fuchsia-600 px-3 py-2 hover:bg-fuchsia-500">
                  {isPlaying ? (
                    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                      <path d="M7 5.5h3.5v13H7v-13Zm6.5 0H17v13h-3.5v-13Z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                      <path d="M8 5.25v13.5L18.5 12 8 5.25Z" />
                    </svg>
                  )}
                </button>
                <button type="button" onClick={() => selectTrack(currentIndex + 1)} aria-label="Next track" className="rounded-lg px-2 py-1 hover:bg-white/10">
                  &#9197;
                </button>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold" title={formatTrackLabel(currentTrack)}>
                    {formatTrackLabel(currentTrack)}
                  </div>
                  <div className="text-xs text-white/60">
                    {autoplayBlocked ? "Select play to start music" : `${currentIndex + 1} / ${playlist.length}`}
                  </div>
                </div>
                <button type="button" onClick={() => setMuted((value) => !value)} aria-label={muted ? "Unmute music" : "Mute music"} className="rounded-lg px-2 py-1 hover:bg-white/10">
                  {muted ? (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9 6.5 12H3.75v4h2.75l3.25 3V9Zm4.5 3.25 5 5m0-5-5 5" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 5 6.5 8H3.75v8h2.75l3.25 3V5Zm4.1 4.15a4 4 0 0 1 0 5.7m2.55-8.25a7.5 7.5 0 0 1 0 10.8" />
                    </svg>
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={volume}
                  onChange={(event) => setVolume(Number(event.target.value))}
                  aria-label="Music volume"
                  className="w-20 accent-fuchsia-500"
                />
              </div>

              <div>
                <label htmlFor={`music-search-${gameId}`} className="sr-only">Search music by title, artist, or tag</label>
                <input
                  id={`music-search-${gameId}`}
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  onFocus={() => onSearchFocusChange?.(true)}
                  onBlur={() => onSearchFocusChange?.(false)}
                  onKeyDown={(event) => event.stopPropagation()}
                  onKeyUp={(event) => event.stopPropagation()}
                  data-unity-keyboard-bypass="true"
                  placeholder="Search by title, artist, or tag..."
                  autoComplete="off"
                  className="w-full rounded-lg border border-white/15 bg-white/10 px-3 py-2 text-sm text-white outline-none placeholder:text-white/45 focus:border-fuchsia-400"
                />
              </div>

              {searchQuery.trim() && (
                <div className="max-h-40 overflow-y-auto rounded-lg border border-white/10 bg-slate-900/95" role="listbox" aria-label="Music search results">
                  {searchResults.length ? searchResults.map((track) => (
                    <button
                      key={track.id}
                      type="button"
                      role="option"
                      aria-selected={track.id === currentTrack.id}
                      onClick={() => selectSearchResult(track)}
                      className="block w-full border-b border-white/5 px-3 py-2 text-left last:border-b-0 hover:bg-white/10"
                    >
                      <span className="block truncate text-sm font-medium">{formatTrackLabel(track)}</span>
                      {!!track.tags?.length && (
                        <span className="block truncate text-xs text-white/50">{track.tags.join(" · ")}</span>
                      )}
                    </button>
                  )) : (
                    <div className="px-3 py-3 text-sm text-white/55">No matching music found.</div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </>
  );
}
